import React from 'react';
import { gql, useQuery } from '@apollo/client';
import { generatePath, useHistory } from 'react-router';
import { Card } from 'react-bootstrap';
import './dashboard.scss';
import { getRouteByName } from 'app/routes';
import { t } from 'i18next';
import { CollectionOrderStates, UserTypeValues } from 'shared-pages';
import { useRootStore } from 'store';

export const Dashboard = () => {
  const history = useHistory<any>();
  const { appStore, authStore } = useRootStore();
  const isCollector = appStore.usertypeId === UserTypeValues.Collector;
  const isPlatformOwner = appStore.usertypeId === UserTypeValues.PlatformOwner;
  const isCollectionPoint = appStore.usertypeId === UserTypeValues.CollectionPoint;
  const { data: rawData } = useQuery(
    gql`
      query getDashboardIndexes {
        getDashboardIndexes {
          todayCollectionOrders
          todayExecutedCollections
          pendingCollectionOrders
          todayContainerOrders
          pendingContainerOrders
          pendingCollectionpointsRequests
        }
      }
    `,
    { fetchPolicy: 'network-only' },
  );
  const {
    todayCollectionOrders,
    todayExecutedCollections,
    pendingCollectionOrders,
    todayContainerOrders,
    pendingContainerOrders,
    pendingCollectionpointsRequests,
  } = rawData?.getDashboardIndexes ?? {};

  enum IdxEnum {
    todayCollectionOrders,
    todayExecutedCollections,
    pendingCollectionOrders,
    todayContainerOrders,
    pendingContainerOrders,
    pendingCollectionpointsRequests,
  }
  const cardClicked = (value: IdxEnum) => {
    let loc: any;

    // Helper function to determine card color based on badge content
    if (value === IdxEnum.todayCollectionOrders)
      loc = {
        pathname: generatePath(getRouteByName('Orders')),
        state: { filterValues: { startDate: new Date(), endDate: new Date() } },
      };
    else if (value === IdxEnum.pendingCollectionOrders) {
      const statuses =
        isPlatformOwner || isCollectionPoint
          ? [
              CollectionOrderStates.ApplicationSubmitted,
              CollectionOrderStates.OrganizationCompleted,
              CollectionOrderStates.PackageShipmentCompleted,
              CollectionOrderStates.CollectorOrder,
              CollectionOrderStates.ScheduledCollectorCollection,
            ]
          : isCollector
          ? [
              CollectionOrderStates.OrganizationCompleted,
              CollectionOrderStates.PackageShipmentCompleted,
              CollectionOrderStates.CollectorOrder,
              CollectionOrderStates.ScheduledCollectorCollection,
            ]
          : [];
      loc = {
        pathname: generatePath(getRouteByName('Orders')),
        state: {
          filterValues: {
            statusId: statuses,
          },
        },
      };
    } else if (value === IdxEnum.todayExecutedCollections)
      loc = {
        pathname: generatePath(getRouteByName('Collections')),
        state: { filterValues: { startDate: new Date(), endDate: new Date() } },
      };
    else if (value === IdxEnum.todayContainerOrders)
      loc = {
        pathname: generatePath(getRouteByName('ContainerOrders')),
        state: { filterValues: { startDate: new Date(), endDate: new Date() } },
      };
    else if (value === IdxEnum.pendingContainerOrders)
      loc = {
        pathname: generatePath(getRouteByName('ContainerOrders')),
        state: { filterValues: { startDate: new Date(), endDate: new Date() } },
      };
    else if (value === IdxEnum.pendingCollectionpointsRequests)
      loc = {
        pathname: generatePath(getRouteByName('CollectionpointRequests')),
        state: { filterValues: { isApproved: 'wait' } },
      };
    else loc = { pathname: generatePath(getRouteByName('Users')), state: { filterValues: {} } };
    history.push(loc);
  };

  const getCardColorClass = (badgeText: string): string => {
    if (badgeText.toLowerCase().includes('εκκρεμε')) {
      return 'dash-card-red'; // Red for pending items
    }
    if (badgeText.toLowerCase().includes('σήμερα')) {
      return 'dash-card-blue'; // Blue for today items
    }
    return 'dash-card-green'; // Green for everything else
  };
  return (
    <div className="mt-5">
      {rawData && (
        <div className="dashboard-cards-container">
          {/* Column 1 : Σημερινά Αιτήματα Συλλογής */}
          {isPlatformOwner && (
            <div className="dashboard-card-wrapper">
              <Card
                className={`dash-card ${getCardColorClass('Σήμερα')}`}
                onClick={() => cardClicked(IdxEnum.todayCollectionOrders)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && cardClicked(IdxEnum.todayCollectionOrders)}
                aria-label={`${todayCollectionOrders} ${t('Σημερινά Αιτήματα Συλλογής')}. ${t(
                  'κλικ για περισσότερα',
                )}`}
              >
                <blockquote className="blockquote mb-0 card-body">
                  <h1 className="outline-text">{todayCollectionOrders}</h1>
                  <footer>{t('Σημερινά Αιτήματα Συλλογής')}</footer>
                </blockquote>
                <span className="dash-card-badge">{t('Σήμερα')}</span>
              </Card>
            </div>
          )}
          {/* Column 2 : Εκκρεμή Αιτήματα Συλλογής */}
          {(isPlatformOwner || isCollectionPoint || isCollector) && (
            <div className="dashboard-card-wrapper">
              {/* Pending Collection Orders */}
              <Card
                className={`dash-card ${getCardColorClass('Εκκρεμείς')}`}
                onClick={() => cardClicked(IdxEnum.pendingCollectionOrders)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && cardClicked(IdxEnum.pendingCollectionOrders)}
                aria-label={`${pendingCollectionOrders} ${t('Εκκρεμή Αιτήματα Συλλογής')}. ${t(
                  'κλικ για περισσότερα',
                )}`}
              >
                <blockquote className="blockquote mb-0 card-body">
                  <h1 className="outline-text">{pendingCollectionOrders}</h1>
                  <footer>{t('Εκκρεμή Αιτήματα Συλλογής')}</footer>
                </blockquote>
                <span className="dash-card-badge">{t('Εκκρεμή')}</span>
              </Card>
            </div>
          )}
          {/* Column 3 : Σημερινές Συλλογές*/}
          {(isPlatformOwner || isCollector) && (
            <div className="dashboard-card-wrapper">
              {/* Today's Executed Collections */}
              <Card
                className={`dash-card ${getCardColorClass('Ολοκληρώθηκαν')}`}
                onClick={() => cardClicked(IdxEnum.todayExecutedCollections)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) =>
                  e.key === 'Enter' && cardClicked(IdxEnum.todayExecutedCollections)
                }
                aria-label={`${todayExecutedCollections} ${t('Σημερινές Συλλογές')}. ${t(
                  'κλικ για περισσότερα',
                )}`}
              >
                <blockquote className="blockquote mb-0 card-body">
                  <h1 className="outline-text">{todayExecutedCollections}</h1>
                  <footer>{t('Σημερινές Συλλογές')}</footer>
                </blockquote>
                <span className="dash-card-badge">{t('Ολοκληρώθηκαν')}</span>
              </Card>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
